// Helper function to check if a tool is an image generation tool
export const isImageGenerationTool = (toolName) => {
  if (!toolName) return false;
  return (
    toolName.includes("image_gen") ||
    toolName.includes("dalle") ||
    toolName.includes("midjourney") ||
    toolName.includes("stable_diffusion") ||
    toolName.includes("stability") ||
    toolName.includes("image_generation") ||
    toolName === "image_edit" ||
    toolName.includes("image_edit")
  );
};

// Helper function to check if a tool is a web search tool
export const isWebSearchTool = (toolName) => {
  if (!toolName || typeof toolName !== "string") return false;

  const normalizedToolName = toolName.toLowerCase().trim();

  return (
    normalizedToolName === "web_search" ||
    normalizedToolName === "firecrawl_search" ||
    normalizedToolName === "places_search" ||
    normalizedToolName.includes("web_search") ||
    normalizedToolName.includes("firecrawl_search") ||
    normalizedToolName.includes("places_search") ||
    normalizedToolName.includes("search")
  );
};

// Helper function to check if a tool is a firecrawl search tool (legacy compatibility)
// Note: This is now just an alias for isWebSearchTool for backward compatibility
export const isFirecrawlSearchTool = (toolName) => {
  return isWebSearchTool(toolName);
};

// Helper function to check if a tool is a canvas tool
export const isCanvasTool = (toolName) => {
  if (!toolName) return false;
  return (
    toolName.includes("canvas") ||
    toolName === "canvas_create" ||
    toolName === "canvas_stream" ||
    toolName.includes("canvas_")
  );
};

// Helper function to check if a tool is a canvas creation tool
export const isCanvasCreationTool = (toolName) => {
  if (!toolName) return false;
  return (
    toolName === "canvas_create" ||
    toolName === "canvas_stream" ||
    toolName === "canvas_edit"
  );
};

// Helper function to check if a tool is an image edit tool
export const isImageEditTool = (toolName) => {
  if (!toolName) return false;
  return toolName === "image_edit" || toolName.includes("image_edit");
};

// Helper function to check if a tool is a video generation tool
export const isVideoGenerationTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("video_gen") ||
    normalized.includes("video_generation") ||
    normalized.includes("kling") ||
    normalized.includes("runway") ||
    normalized.includes("pika")
  );
};

// Helper function to check if a tool is a YouTube tool
export const isYouTubeTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("youtube") ||
    normalized.includes("yt_") ||
    normalized === "analyze_video"
  );
};

// Helper function to check if a tool is a Google Calendar tool
export const isCalendarTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("calendar") ||
    normalized.includes("gcal") ||
    normalized.includes("google_calendar")
  );
};

// Helper function to check if a tool is a Gmail tool
export const isGmailTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("gmail") ||
    normalized.includes("email") ||
    normalized.includes("send_email") ||
    normalized.includes("read_email")
  );
};

// Helper function to check if a tool is a Google Drive tool
export const isDriveTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("drive") ||
    normalized.includes("gdrive") ||
    normalized.includes("google_drive")
  );
};

// Helper function to check if a tool is a memory/bio tool
export const isMemoryTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("memory") ||
    normalized.includes("bio") ||
    normalized.includes("remember") ||
    normalized.includes("save_context")
  );
};

// Helper function to check if a tool is a code interpreter tool
export const isCodeInterpreterTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("code_interpreter") ||
    normalized.includes("execute_code") ||
    normalized.includes("run_code") ||
    normalized.includes("python") ||
    normalized === "code"
  );
};

// Helper function to check if a tool is a places search tool
export const isPlacesSearchTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return normalized.includes("places_search") || normalized.includes("places");
};

// Helper function to check if a tool is a firecrawl tool
export const isFirecrawlTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return (
    normalized.includes("firecrawl") ||
    normalized.includes("crawl") ||
    normalized.includes("scrape")
  );
};

// Helper function to check if a tool is an MCP tool
export const isMcpTool = (toolName) => {
  if (!toolName) return false;
  const normalized = toolName.toLowerCase();
  return normalized.includes("mcp_") || normalized.startsWith("mcp");
};

/**
 * Get the normalized tool category from a raw tool name
 * Used by the skeleton loader to determine which message to display
 *
 * @param {string} toolName - The raw tool name from the API
 * @returns {string} - Normalized category: 'web_search', 'image_generation', 'image_edit', etc.
 */
export const getToolCategory = (toolName) => {
  if (!toolName) return "unknown";

  // Check in order of specificity (more specific checks first)
  if (isImageEditTool(toolName)) return "image_edit";
  if (isImageGenerationTool(toolName)) return "image_generation";
  if (isVideoGenerationTool(toolName)) return "video_generation";
  if (isYouTubeTool(toolName)) return "youtube";
  if (isCalendarTool(toolName)) return "calendar";
  if (isGmailTool(toolName)) return "gmail";
  if (isDriveTool(toolName)) return "drive";
  if (isMemoryTool(toolName)) return "memory";
  if (isCodeInterpreterTool(toolName)) return "code_interpreter";
  if (isCanvasCreationTool(toolName)) return "canvas";
  if (isCanvasTool(toolName)) return "canvas";
  if (isPlacesSearchTool(toolName)) return "places_search";
  if (isFirecrawlTool(toolName)) return "firecrawl";
  if (isMcpTool(toolName)) return "mcp";
  if (isWebSearchTool(toolName)) return "web_search";

  return "unknown";
};
